import React, { useEffect, useMemo, useState } from 'react';
import { statService } from '../../../services/StatService';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Pie,
  PieChart,
  TooltipContentProps,
  PieLabelRenderProps,
  BarChart,
  Bar,
  BarShapeProps,
  Rectangle,
  ResponsiveContainer,
} from 'recharts';
import { StatsModel } from '../../../models/stats.model';
import { useQuery } from '@tanstack/react-query';
import { Card } from 'flowbite-react';
import { plan$ } from '../../../services/UserService';
import { PlanModel } from '../../../models/plan.model';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
const MINIMUM_SEPARATE_SLICE_PERCENTAGE = 0.05;
const DASHBOARD_COLUMN_HEIGHT = 'h-[36rem]';
const CARD_HEADER_CLASS = 'mb-5 flex h-8 items-start';
const CARD_TITLE_CLASS = 'text-gray-100 text-xl text-bold font-bold';

const formatPercentage = (percentage: number) =>
  percentage > 0 && percentage < 0.01 ? '<0.01%' : `${percentage.toFixed(2)}%`;

export function Analytics() {
  const RADIAN = Math.PI / 180;
  const [plan, setPlan] = useState<PlanModel>();
  const { data: stats, isFetched } = useQuery<StatsModel>({
    queryKey: ['stats'],
    queryFn: () => statService.getStats(),
    refetchInterval: 60_000,
  });

  useEffect(() => {
    var subscription = plan$.subscribe((plan: PlanModel) => {
      setPlan(plan);
    });
    return () => subscription.unsubscribe();
  }, []);

  const sumRequests = (byTemplate?: Record<string, number>) =>
    Object.values(byTemplate ?? {}).reduce((total, count) => total + count, 0);

  const templateUsage = useMemo(
    () =>
      Object.entries(stats?.year?.byTemplate ?? {})
        .map(([templateId, value], index) => ({
          id: templateId,
          name: stats?.templateIdNames?.[templateId] ?? `Template ${templateId}`,
          value,
          fill: COLORS[index % COLORS.length],
        }))
        .sort((a, b) => b.value - a.value),
    [stats],
  );

  const totalRequests = useMemo(
    () => templateUsage.reduce((total, template) => total + template.value, 0),
    [templateUsage],
  );

  const templateChartData = useMemo(() => {
    if (totalRequests === 0) return [];

    const separateSlices = templateUsage.filter(
      (template) => template.value / totalRequests > MINIMUM_SEPARATE_SLICE_PERCENTAGE,
    );
    const otherTemplates = templateUsage.filter(
      (template) => template.value / totalRequests <= MINIMUM_SEPARATE_SLICE_PERCENTAGE,
    );
    const otherRequests = otherTemplates.reduce((total, template) => total + template.value, 0);

    if (otherRequests === 0) return separateSlices;

    return [
      ...separateSlices,
      {
        id: 'other',
        name: 'Other',
        value: otherRequests,
        fill: COLORS[separateSlices.length % COLORS.length],
        templates: otherTemplates.map((template) => ({
          ...template,
          percentage: (template.value / totalRequests) * 100,
        })),
      },
    ];
  }, [templateUsage, totalRequests]);

  const hourlyData = useMemo(
    () =>
      (stats?.hourlyLast12 ?? []).map((record) => ({
        name: `${record.hourUtc?.getHours().toString().padStart(2, '0') ?? '--'}:00`,
        total: sumRequests(record.byTemplate),
      })),
    [stats],
  );

  const monthlyData = useMemo(
    () =>
      (stats?.months ?? [])
        .map((record) => {
          const monthDate =
            record.hourUtc ??
            (record.year && record.month
              ? new Date(Date.UTC(record.year, record.month - 1, 1))
              : undefined);

          return {
            name: monthDate
              ? monthDate.toLocaleString(undefined, { month: 'short', year: 'numeric' })
              : 'Unknown month',
            timestamp: monthDate?.getTime() ?? 0,
            total: sumRequests(record.byTemplate),
          };
        })
        .sort((left, right) => left.timestamp - right.timestamp),
    [stats],
  );

  const CustomTemplateTooltip = ({ active, payload }: TooltipContentProps) => {
    if (!active || !payload || !payload.length) return null;

    const item = payload[0].payload;
    const percentage = totalRequests > 0 ? (Number(item.value) / totalRequests) * 100 : 0;

    return (
      <div
        className="min-w-64 max-w-80 text-left"
        style={{
          background: '#171D1E',
          border: '1px solid #276261',
          padding: '8px',
          borderRadius: '6px',
          color: '#F3F4EF',
        }}
      >
        <div className="font-semibold">{item.name}</div>
        <div>
          Requests: {Number(item.value).toLocaleString()} ({formatPercentage(percentage)})
        </div>
        {item.id === 'other' && Array.isArray(item.templates) && (
          <div className="mt-2 border-t border-white/10 pt-2">
            {item.templates.map((template: { id: string; name: string; percentage: number }) => (
              <div key={template.id} className="flex justify-between gap-4 py-0.5 text-sm">
                <span className="min-w-0 break-words">{template.name}</span>
                <span className="shrink-0 text-gray-300">
                  {formatPercentage(template.percentage)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const CustomLineTooltip = ({ active, payload }: TooltipContentProps) => {
    if (!active || !payload || !payload.length) return null;

    const item = payload[0].payload;

    return (
      <div
        style={{
          background: '#171D1E',
          border: '1px solid #276261',
          padding: '8px',
          borderRadius: '6px',
          color: '#F3F4EF',
        }}
      >
        <div>{item.name}</div>
        <div>Requests: {item.total}</div>
      </div>
    );
  };

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }: PieLabelRenderProps) => {
    if (cx == null || cy == null || innerRadius == null || outerRadius == null) {
      return null;
    }
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5 - 55;
    const ncx = Number(cx);
    const x = ncx + radius * Math.cos(-(midAngle ?? 0) * RADIAN);
    const ncy = Number(cy);
    const y = ncy + radius * Math.sin(-(midAngle ?? 0) * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > ncx ? 'start' : 'end'}
        dominantBaseline="central"
        className="font-bold text-lg"
      >
        {`${((percent ?? 1) * 100).toFixed(0)}%`}
      </text>
    );
  };

  const CustomFillRectangle = (props: BarShapeProps) => {
    return <Rectangle {...props} fill="#8884d8" />;
  };

  const ActiveRectangle = (props: BarShapeProps) => {
    return <CustomFillRectangle {...props} stroke="orange" strokeWidth={3} />;
  };

  return (
    <div className="step-1">
      {isFetched && stats ? (
        <div>
          <Card
            className="bg-transparent"
            theme={{
              root: {
                children: 'flex h-full min-h-0 flex-1 flex-col p-4',
              },
            }}
          >
            <h2 className="text-gray-100 text-xl text-bold mb-5 font-bold">12 hour requests</h2>
            <AreaChart
              style={{ width: '100%', maxHeight: '20vh', aspectRatio: 1.618 }}
              data={hourlyData}
              margin={{
                top: 20,
                right: 0,
                left: 0,
                bottom: 0,
              }}
              responsive={true}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" niceTicks="snap125" />
              <YAxis width="auto" niceTicks="snap125" />
              <Tooltip content={CustomLineTooltip} />
              <Area type="monotone" dataKey="total" stroke="#8884d8" fill="#8884d8" />
            </AreaChart>
          </Card>

          <div className="mb-6 grid grid-cols-1 items-stretch gap-6 xl:grid-cols-3">
            <div className={`w-full py-5 text-center ${DASHBOARD_COLUMN_HEIGHT}`}>
              <Card className="flex h-full flex-col bg-transparent">
                <div className={`${CARD_HEADER_CLASS} justify-center`}>
                  <h2 className={CARD_TITLE_CLASS}>Templates chart</h2>
                </div>
                <PieChart
                  style={{
                    width: '100%',
                    height: '24rem',
                    maxWidth: '500px',
                    aspectRatio: 1,
                    margin: '0 auto',
                  }}
                  responsive
                >
                  <Pie
                    data={templateChartData}
                    innerRadius="80%"
                    outerRadius="100%"
                    // Corner radius is the rounded edge of each pie slice
                    cornerRadius="50%"
                    fill="#8884d8"
                    label={renderCustomizedLabel}
                    labelLine={false}
                    // padding angle is the gap between each pie slice
                    paddingAngle={5}
                    dataKey="value"
                    isAnimationActive={true}
                    activeShape={{ stroke: '#ffffff', strokeWidth: 2 }}
                  />
                  <Tooltip content={CustomTemplateTooltip} />
                </PieChart>
              </Card>
            </div>
            <div className={`w-full py-5 text-center ${DASHBOARD_COLUMN_HEIGHT}`}>
              <Card
                className="flex h-full min-h-0 flex-col overflow-hidden bg-transparent"
                theme={{
                  root: {
                    children: 'flex h-full min-h-0 flex-1 flex-col p-4',
                  },
                }}
              >
                <div className={`${CARD_HEADER_CLASS} justify-center`}>
                  <h3 className={CARD_TITLE_CLASS}>Template usage</h3>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto border-t border-white/10 pr-2">
                  <dl className="divide-y divide-white/10">
                    {templateUsage.map((template) => (
                      <div
                        key={template.id}
                        className="px-1 py-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0"
                      >
                        <dt className="text-sm/6 font-medium sm:col-span-2 text-gray-100">
                          {template.name}
                        </dt>
                        <dd className="mt-1 text-sm/6 text-gray-400 sm:mt-0">
                          {template.value.toLocaleString()}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </Card>
            </div>
            <div className={`flex w-full flex-col py-5 text-center ${DASHBOARD_COLUMN_HEIGHT}`}>
              <Card
                className="flex min-h-0 flex-1 flex-col bg-transparent"
                theme={{
                  root: {
                    children: 'flex h-full min-h-0 flex-1 flex-col p-4',
                  },
                }}
              >
                <div className={`${CARD_HEADER_CLASS} justify-center`}>
                  <h3 className={CARD_TITLE_CLASS}>Monthly</h3>
                </div>
                <div className="min-h-0 flex flex-1">
                  <div className="h-full w-full max-w-[700px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart layout="vertical" data={monthlyData}>
                        <Tooltip shared={false} content={CustomLineTooltip} />
                        <XAxis
                          type="number"
                          height={50}
                          label={{ value: 'Requests', position: 'insideBottomRight' }}
                        />
                        <YAxis type="category" dataKey="name" interval={0} width="auto" />
                        <Bar
                          dataKey="total"
                          radius={5}
                          shape={CustomFillRectangle}
                          activeBar={ActiveRectangle}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Card>

              <Card
                className="mt-5 shrink-0 bg-transparent"
                theme={{
                  root: {
                    children: 'flex h-full min-h-0 flex-1 flex-col p-4',
                  },
                }}
              >
                <div className={`${CARD_HEADER_CLASS} justify-center`}>
                  <h3 className={CARD_TITLE_CLASS}>Expances</h3>
                </div>
                {plan ? (
                  <div className="border-t border-white/10">
                    <dl className="divide-y divide-white/10">
                      <div className="px-1 py-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                        <dt className="text-sm/6 font-medium text-gray-100 sm:col-span-2">
                          This month
                        </dt>
                        <dd className="mt-1 text-sm/6 text-gray-400 sm:mt-0">
                          $
                          {(
                            plan.pricePerRequest! * sumRequests(stats.monthCurrent?.byTemplate)
                          ).toFixed(3)}
                        </dd>
                      </div>
                      <div className="px-1 py-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                        <dt className="text-sm/6 font-medium text-gray-100 sm:col-span-2">Total</dt>
                        <dd className="mt-1 text-sm/6 text-gray-400 sm:mt-0">
                          ${(plan.pricePerRequest! * totalRequests).toFixed(3)}
                        </dd>
                      </div>
                    </dl>
                  </div>
                ) : (
                  <div>Loading...</div>
                )}
              </Card>
            </div>
          </div>
        </div>
      ) : (
        <>Loading...</>
      )}
    </div>
  );
}
